// Shared look & feel for the QR Code module (presentation only).
import { Avatar, Box, Button, CircularProgress, DialogTitle, IconButton, Stack, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

export const QR = {
  main: "#198754",
  light: "#45c57f",
  dark: "#0f5132",
  amber: "#d97706",
  blue: "#2563eb",
  purple: "#7c3aed",
  slate: "#64748b",
  border: "#e9edf2",
};

// White card with a green top strip and soft shadow
export const qrCardSx = {
  borderRadius: 3,
  border: `1px solid ${QR.border}`,
  borderTop: `3px solid ${QR.main}`,
  boxShadow: "0 1px 3px rgba(16,24,40,0.06)",
  overflow: "hidden",
};

// Dialog paper used by all QR modals
export const qrDialogPaperSx = {
  borderRadius: { xs: 2.5, sm: 3 },
  overflow: "hidden",
  backgroundColor: "#fff",
  boxShadow: "0 24px 60px -20px rgba(15,23,42,0.45)",
  // Phones: nearly full screen with an 8px edge; larger screens: normal dialog near the top
  m: { xs: 1, sm: 4 },
  mt: { xs: 1, sm: 4 },
  width: { xs: "calc(100% - 16px)", sm: undefined },
  maxHeight: { xs: "calc(100% - 16px)", sm: "calc(100% - 64px)" },
  display: "flex",
  flexDirection: "column",
  alignSelf: "flex-start",
};

// Toolbar action button (44px, matches the filter fields).
// variant "soft": white with green border/text; "solid": green gradient.
export function QrActionButton({ icon, label, count, onClick, variant = "soft", color = QR.blue, gradientTo = "#3b82f6", loading = false, loadingLabel }) {
  const solid = variant === "solid";
  return (
    <Button
      onClick={loading ? undefined : onClick}
      disabled={loading}
      sx={{
        "&.Mui-disabled": { opacity: 0.75, color: solid ? "#fff" : color },
        height: 44,
        px: 1.75,
        gap: 1,
        borderRadius: 3,
        textTransform: "none",
        fontSize: 14.5,
        fontWeight: 700,
        whiteSpace: "nowrap",
        color: solid ? "#fff" : color,
        background: solid ? `linear-gradient(135deg, ${color}, ${gradientTo})` : "#fff",
        border: `1.5px solid ${solid ? "transparent" : `${color}55`}`,
        boxShadow: solid ? `0 8px 18px -10px ${color}` : "none",
        transition: "all .15s ease",
        "&:hover": solid
          ? { boxShadow: `0 10px 22px -8px ${color}`, transform: "translateY(-1px)", background: `linear-gradient(135deg, ${color}, ${gradientTo})` }
          : { borderColor: color, bgcolor: `${color}0d` },
        "& .qa-icon": {
          width: 26,
          height: 26,
          borderRadius: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: solid ? "#fff" : color,
          bgcolor: solid ? "rgba(255,255,255,0.2)" : `${color}14`,
        },
      }}
    >
      <Box className="qa-icon">{loading ? <CircularProgress size={14} sx={{ color: "inherit" }} /> : icon}</Box>
      {loading && loadingLabel ? loadingLabel : label}
      {count !== undefined && (
        <Box
          component="span"
          sx={{
            minWidth: 22,
            height: 22,
            px: 0.75,
            borderRadius: 5,
            fontSize: 12,
            fontWeight: 800,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            color: solid ? color : "#fff",
            bgcolor: solid ? "#fff" : color,
          }}
        >
          {count}
        </Box>
      )}
    </Button>
  );
}

// Gradient modal header: icon tile + title/subtitle + close button
export function QrModalHeader({ icon, title, subtitle, onClose, gradient }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1.5,
        px: { xs: 2, sm: 2.5 },
        py: { xs: 1.25, sm: 1.5 },
        flexShrink: 0,
        color: "#fff",
        background: gradient || `linear-gradient(120deg, ${QR.dark} 0%, ${QR.main} 60%, ${QR.light} 100%)`,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
        <Avatar variant="rounded" sx={{ width: 38, height: 38, borderRadius: 2, bgcolor: "rgba(255,255,255,0.18)", color: "#fff" }}>
          {icon}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <DialogTitle sx={{ m: 0, p: 0, fontSize: "1.05rem", fontWeight: 700, lineHeight: 1.3 }}>{title}</DialogTitle>
          {subtitle && (
            <Typography variant="caption" noWrap display="block" sx={{ opacity: 0.85 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </Stack>
      <IconButton
        onClick={onClose}
        aria-label="close"
        sx={{ color: "#fff", bgcolor: "rgba(255,255,255,0.12)", "&:hover": { bgcolor: "rgba(255,255,255,0.24)" } }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}
